<script setup lang="ts">
    import { computed } from 'vue'

    import type { ColumnReadModel, ValueSerieReadModel } from '@Quest/_Common/readmodels'
    import type { MonthYear } from '@common/types'
    import { MonthYearHelpers } from '@common/types'

    // ---------------- props ----------------

    const props = defineProps<{
        propTitle?: string
        propSeries: ValueSerieReadModel[]
        propColumns: ColumnReadModel[]
        propStartDate?: MonthYear
    }>()

    // ---------------- computed (replacing getters) ----------------

    const title = computed(() => props.propTitle ?? '')
    const series = computed(() => props.propSeries)
    const columns = computed(() => props.propColumns)
    const startDate = computed<MonthYear | null>(() => props.propStartDate ?? null)

    const totalSerie = computed(() =>
        series.value.find(s => s.IsTotal)
    )

    const regularSeries = computed(() =>
        series.value.filter(s => !s.IsTotal)
    )

    const startDateLabel = computed<string | null>(() =>
        MonthYearHelpers.toMonthYearLongString(startDate.value)
    )
</script>


<template>
    <div>
        <div class="table-wrapper">
            <table class="table table-bordered">
                <thead>
                    <tr v-if="startDate">
                        <td class="sticky-col">
                            Start date: <span class="font-italic pointer" title="Dated labels align project periods (e.g., months, quarters, or years) with calendar dates based on the project start date.">
                                {{startDateLabel}}
                            </span>
                        </td>
                        <td class="period-col" v-for="col in columns" :key="col.Index" :class="col.CssClass">
                            {{col.DatedLabel}}
                        </td>
                    </tr>
                    <tr>
                        <th class="sticky-col">
                            {{ title }}
                        </th>
                        <th class="period-col" v-for="col in columns" :key="col.Index">
                            {{col.Label}}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="s in regularSeries" :key="s.Id">
                        <td class="sticky-col">
                            <span>{{s.Name}}</span>
                            <span v-if="s.Unit">[{{s.Unit}}]</span>
                        </td>
                        <td class="period-col" v-for="col in columns" :key="col.Index">
                            <span>{{ s.FormattedValues[col.Index] }}</span>
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="totalSerie">
                    <tr>
                        <th class="sticky-col">
                            <span>{{totalSerie.Name}}</span>
                            <span v-if="totalSerie.Unit">[{{totalSerie.Unit}}]</span>
                        </th>
                        <td class="period-col" v-for="col in columns" :key="col.Index">
                            <span>{{ totalSerie.FormattedValues[col.Index]}}</span>
                        </td>
                    </tr>
                    <tr v-if="totalSerie.ShowGrowthValues">
                        <th class="sticky-col">
                            % Growth
                        </th>
                        <td class="period-col" v-for="col in columns" :key="col.Index">
                            <span>{{ totalSerie.FormattedGrowthValues[col.Index] }}</span>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>


<style scoped>
    .table-wrapper {
        overflow-x: auto;
        max-width: 100%;
        margin-bottom: 100px;
    }

    .table {
        margin-bottom: 0;
        position: relative;
        z-index: 1;
    }

    .sticky-col {
        position: sticky;
        left: 0;
        z-index: 2;
        background-color: white;
        width: 350px;
        min-width: 350px;
    }


    /* Styling for month headers */
    .period-col {
        text-align: center;
        font-weight: 500;
        border-right: 1px solid #f5f5f5;
    }

    td.period-col {
        padding: 2px;
        vertical-align: middle;
    }

    /* Past Period: Light Gray */
    .past {
        background-color: #e0e0e0; /* Light gray */
        color: #555555; /* Optional: Darker text for contrast */
    }

    /* Current Period (Now): Soft Blue */
    .now {
        background-color: #a2c4f2; /* Soft blue */
        border: 1px solid #5a9bd5; /* Optional: Slightly darker blue border */
    }

    /* Commitment Period: Light Blue */
    .commitment {
        background-color: #d3e5fa; /* Light blue */
        border: 1px solid #a2c4f2; /* Optional: Slightly darker border */
    }

    /* Future Period: Pale Blue */
    .future {
        background-color: #ebf4fc; /* Pale blue */
        border: 1px dashed #d3e5fa; /* Optional: Dashed border for distinction */
    }
</style>